import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createRoot } from "react-dom/client";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { RoundedBox, Text } from "@react-three/drei";
import * as THREE from "three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { OutputPass } from "three/examples/jsm/postprocessing/OutputPass.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { ShaderPass } from "three/examples/jsm/postprocessing/ShaderPass.js";
import Lenis from "lenis";
import "@fontsource-variable/space-grotesk";
import "@fontsource/space-mono/400.css";
import spaceMonoFont from "@fontsource/space-mono/files/space-mono-latin-400-normal.woff?url";
import "./styles.css";

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const MAX_RIPPLES = 14;

const RippleShader = {
  uniforms: {
    tDiffuse: { value: null },
    uResolution: { value: new THREE.Vector2(1, 1) },
    uRipples: {
      value: Array.from(
        { length: MAX_RIPPLES },
        () => new THREE.Vector4(-10, -10, 10, 0),
      ),
    },
  },
  vertexShader: /* glsl */ `
    varying vec2 vUv;

    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform vec2 uResolution;
    uniform vec4 uRipples[${MAX_RIPPLES}];
    varying vec2 vUv;

    void main() {
      vec2 uv = vUv;
      float aspect = uResolution.x / max(uResolution.y, 1.0);
      vec2 displacement = vec2(0.0);
      float crest = 0.0;

      for (int i = 0; i < ${MAX_RIPPLES}; i++) {
        vec4 ripple = uRipples[i];
        float age = ripple.z;
        float strength = ripple.w;

        if (strength > 0.001 && age < 1.55) {
          vec2 delta = uv - ripple.xy;
          delta.x *= aspect;

          float distanceToCenter = length(delta);
          float radius = age * 0.31;
          float envelope = exp(-age * 1.9) * strength;
          float ringDistance = distanceToCenter - radius;
          float wave = sin(ringDistance * 118.0);
          float localizedWave = wave * exp(-abs(ringDistance) * 52.0) * envelope;
          vec2 direction = delta / max(distanceToCenter, 0.0001);

          direction.x /= aspect;
          displacement += direction * localizedWave * 0.0105;
          crest += exp(-pow(ringDistance * 58.0, 2.0)) * envelope;
        }
      }

      vec2 sampleUv = clamp(uv - displacement, vec2(0.001), vec2(0.999));
      vec2 chroma = displacement * 0.42;
      vec4 color = texture2D(tDiffuse, sampleUv);

      color.r = texture2D(tDiffuse, clamp(sampleUv + chroma, 0.001, 0.999)).r;
      color.b = texture2D(tDiffuse, clamp(sampleUv - chroma, 0.001, 0.999)).b;
      color.rgb += crest * vec3(0.13, 0.18, 0.26);

      gl_FragColor = color;
    }
  `,
};

function createStarGeometry(points = 5, outer = 0.75, inner = 0.34) {
  const shape = new THREE.Shape();

  for (let index = 0; index < points * 2; index += 1) {
    const radius = index % 2 === 0 ? outer : inner;
    const angle = -Math.PI / 2 + (index * Math.PI) / points;
    const x = Math.cos(angle) * radius;
    const y = Math.sin(angle) * radius;
    if (index === 0) shape.moveTo(x, y);
    else shape.lineTo(x, y);
  }

  shape.closePath();
  return new THREE.ExtrudeGeometry(shape, {
    depth: 0.12,
    bevelEnabled: true,
    bevelSegments: 4,
    bevelSize: 0.045,
    bevelThickness: 0.045,
  }).center();
}

function useReducedMotion() {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  return reduced;
}

function WordMark({ pointerRef, progressRef, reducedMotion }) {
  const group = useRef();
  const size = useThree((state) => state.size);
  const depthLayers = useMemo(() => Array.from({ length: 10 }), []);

  useFrame((_, delta) => {
    if (!group.current) return;
    const pointer = pointerRef.current;
    const progress = progressRef.current;
    const motion = reducedMotion ? 0 : 1;

    group.current.rotation.x = THREE.MathUtils.damp(
      group.current.rotation.x,
      -pointer.y * 0.13 * motion + progress * 0.18,
      4.6,
      delta,
    );
    group.current.rotation.y = THREE.MathUtils.damp(
      group.current.rotation.y,
      pointer.x * 0.22 * motion - progress * 0.32,
      4.6,
      delta,
    );
    group.current.rotation.z = THREE.MathUtils.damp(
      group.current.rotation.z,
      -pointer.x * 0.025 * motion,
      3.8,
      delta,
    );
    group.current.position.y = THREE.MathUtils.damp(
      group.current.position.y,
      -0.28 + progress * 0.7,
      4,
      delta,
    );
    const responsiveScale = size.width < 600 ? 0.57 : size.width < 900 ? 0.78 : 1;
    const scale = responsiveScale * (1 - progress * 0.12);
    group.current.scale.setScalar(
      THREE.MathUtils.damp(group.current.scale.x, scale, 4, delta),
    );
  });

  return (
    <group ref={group} position={[0.25, -0.28, 0]}>
      {depthLayers.map((_, index) => (
        <Text
          key={index}
          font={spaceMonoFont}
          fontSize={2.1}
          letterSpacing={-0.08}
          anchorX="center"
          anchorY="middle"
          position={[0, 0, -0.055 * (index + 1)]}
        >
          HELLO
          <meshStandardMaterial
            color={index < 4 ? "#16166f" : "#111352"}
            roughness={0.38}
            metalness={0.12}
          />
        </Text>
      ))}
      <Text
        font={spaceMonoFont}
        fontSize={2.1}
        letterSpacing={-0.075}
        anchorX="center"
        anchorY="middle"
        position={[0, 0, 0.025]}
      >
        HELLO
        <meshPhysicalMaterial
          color="#382bcf"
          roughness={0.16}
          metalness={0.18}
          clearcoat={1}
          clearcoatRoughness={0.09}
          envMapIntensity={1.2}
        />
      </Text>
    </group>
  );
}

function SmileySticker({ color = "#ffd353" }) {
  return (
    <group>
      <mesh>
        <cylinderGeometry args={[0.62, 0.62, 0.12, 48]} />
        <meshPhysicalMaterial color={color} roughness={0.36} clearcoat={0.4} />
      </mesh>
      <mesh position={[-0.21, 0.08, 0.12]} rotation={[Math.PI / 2, 0, 0]}>
        <capsuleGeometry args={[0.055, 0.16, 6, 12]} />
        <meshBasicMaterial color="#151720" />
      </mesh>
      <mesh position={[0.21, 0.08, 0.12]} rotation={[Math.PI / 2, 0, 0]}>
        <capsuleGeometry args={[0.055, 0.16, 6, 12]} />
        <meshBasicMaterial color="#151720" />
      </mesh>
      <mesh position={[0, -0.22, 0.13]} rotation={[0, 0, Math.PI]}>
        <torusGeometry args={[0.25, 0.035, 8, 32, Math.PI]} />
        <meshBasicMaterial color="#151720" />
      </mesh>
    </group>
  );
}

function OrbitSticker({ color = "#dff4e0" }) {
  return (
    <group rotation={[0.3, 0.25, 0]}>
      <mesh>
        <torusGeometry args={[0.48, 0.1, 14, 48]} />
        <meshPhysicalMaterial color={color} roughness={0.28} clearcoat={0.6} />
      </mesh>
      <mesh rotation={[1.05, 0.5, 0]}>
        <torusGeometry args={[0.48, 0.055, 12, 48]} />
        <meshPhysicalMaterial color="#171928" roughness={0.32} />
      </mesh>
      <mesh>
        <sphereGeometry args={[0.14, 24, 24]} />
        <meshPhysicalMaterial color="#b9ff37" emissive="#4e6e0a" />
      </mesh>
    </group>
  );
}

function PixelSticker({ color = "#8cb4ff" }) {
  const blocks = [
    [-0.42, 0.26],
    [-0.14, 0.26],
    [0.14, 0.26],
    [-0.14, -0.02],
    [0.14, -0.02],
    [0.42, -0.02],
    [0.14, -0.3],
  ];

  return (
    <group>
      {blocks.map(([x, y]) => (
        <RoundedBox
          key={`${x}-${y}`}
          args={[0.3, 0.3, 0.12]}
          radius={0.045}
          smoothness={3}
          position={[x, y, 0]}
        >
          <meshPhysicalMaterial color={color} roughness={0.3} clearcoat={0.5} />
        </RoundedBox>
      ))}
    </group>
  );
}

function StarSticker({ color = "#f6df6b", geometry }) {
  return (
    <mesh geometry={geometry}>
      <meshPhysicalMaterial color={color} roughness={0.3} clearcoat={0.5} />
    </mesh>
  );
}

function FloatingSticker({
  config,
  pointerRef,
  progressRef,
  reducedMotion,
  starGeometry,
}) {
  const group = useRef();
  const velocity = useRef(new THREE.Vector3(...config.velocity));

  useFrame((_, delta) => {
    if (!group.current || reducedMotion) return;
    const dt = Math.min(delta, 0.034);
    const progressBoost = 1 + progressRef.current * 0.65;
    const position = group.current.position;
    const pointer = pointerRef.current;
    const pointerWorldX = pointer.x * 7.4;
    const pointerWorldY = pointer.y * 4.6;
    const dx = position.x - pointerWorldX;
    const dy = position.y - pointerWorldY;
    const distanceSq = dx * dx + dy * dy;

    if (distanceSq < 3.2 && distanceSq > 0.001) {
      const force = (3.2 - distanceSq) * 0.42;
      velocity.current.x += (dx / Math.sqrt(distanceSq)) * force * dt;
      velocity.current.y += (dy / Math.sqrt(distanceSq)) * force * dt;
    }

    position.x += velocity.current.x * dt * progressBoost;
    position.y += velocity.current.y * dt * progressBoost;
    group.current.rotation.z += config.spin * dt * progressBoost;
    group.current.rotation.x += config.spin * 0.15 * dt;

    velocity.current.multiplyScalar(0.999);

    if (position.x > 8.6) position.x = -8.6;
    if (position.x < -8.6) position.x = 8.6;
    if (position.y > 5.5) position.y = -5.5;
    if (position.y < -5.5) position.y = 5.5;
  });

  return (
    <group
      ref={group}
      position={config.position}
      rotation={config.rotation}
      scale={config.scale}
    >
      {config.kind === "smiley" && <SmileySticker color={config.color} />}
      {config.kind === "orbit" && <OrbitSticker color={config.color} />}
      {config.kind === "pixel" && <PixelSticker color={config.color} />}
      {config.kind === "star" && (
        <StarSticker color={config.color} geometry={starGeometry} />
      )}
    </group>
  );
}

function StickerField({ pointerRef, progressRef, reducedMotion }) {
  const starGeometry = useMemo(() => createStarGeometry(), []);
  const stickers = useMemo(
    () => [
      {
        kind: "star",
        color: "#f4da64",
        position: [-6.4, 3.45, 0.4],
        rotation: [0.2, 0.4, -0.3],
        scale: 0.72,
        velocity: [0.32, -0.18, 0],
        spin: 0.24,
      },
      {
        kind: "smiley",
        color: "#ffcd5d",
        position: [4.9, 3.55, 1.1],
        rotation: [1.2, 0.2, 0.25],
        scale: 0.78,
        velocity: [-0.24, -0.22, 0],
        spin: -0.34,
      },
      {
        kind: "orbit",
        color: "#e3f2de",
        position: [5.3, -1.6, 1.7],
        rotation: [0.1, 0.1, 0.2],
        scale: 0.74,
        velocity: [-0.34, 0.19, 0],
        spin: 0.42,
      },
      {
        kind: "pixel",
        color: "#93b8ff",
        position: [-5.1, -2.9, 2.3],
        rotation: [0.45, -0.28, -0.4],
        scale: 0.7,
        velocity: [0.27, 0.3, 0],
        spin: -0.28,
      },
      {
        kind: "star",
        color: "#ff857d",
        position: [1.9, -3.65, 1.9],
        rotation: [0.3, 0.1, 0.48],
        scale: 0.5,
        velocity: [-0.18, 0.34, 0],
        spin: 0.5,
      },
      {
        kind: "orbit",
        color: "#c8ff87",
        position: [-2.2, 3.8, -0.5],
        rotation: [0.2, -0.5, 0.7],
        scale: 0.54,
        velocity: [0.16, -0.28, 0],
        spin: -0.39,
      },
      {
        kind: "smiley",
        color: "#ef9fca",
        position: [-7.2, -0.7, 1.4],
        rotation: [0.7, 0.2, -0.4],
        scale: 0.47,
        velocity: [0.36, 0.13, 0],
        spin: 0.46,
      },
      {
        kind: "pixel",
        color: "#75d6c8",
        position: [7.1, 0.7, 0.7],
        rotation: [0.3, 0.5, 0.9],
        scale: 0.45,
        velocity: [-0.29, -0.16, 0],
        spin: -0.52,
      },
    ],
    [],
  );

  useEffect(() => () => starGeometry.dispose(), [starGeometry]);

  return stickers.map((config, index) => (
    <FloatingSticker
      key={`${config.kind}-${index}`}
      config={config}
      pointerRef={pointerRef}
      progressRef={progressRef}
      reducedMotion={reducedMotion}
      starGeometry={starGeometry}
    />
  ));
}

function PointerGlyph({ pointerRef, reducedMotion }) {
  const glyph = useRef();
  const viewport = useThree((state) => state.viewport);
  const camera = useThree((state) => state.camera);
  const glyphPosition = useMemo(() => new THREE.Vector3(0, 0, 4.5), []);

  useFrame((_, delta) => {
    if (!glyph.current) return;
    const glyphViewport = viewport.getCurrentViewport(camera, glyphPosition);
    const targetX = pointerRef.current.x * (glyphViewport.width / 2);
    const targetY = pointerRef.current.y * (glyphViewport.height / 2);
    const lambda = reducedMotion ? 30 : 18;
    glyph.current.position.x = THREE.MathUtils.damp(
      glyph.current.position.x,
      targetX,
      lambda,
      delta,
    );
    glyph.current.position.y = THREE.MathUtils.damp(
      glyph.current.position.y,
      targetY,
      lambda,
      delta,
    );
  });

  return (
    <mesh ref={glyph} position={[0, 0, 4.5]}>
      <boxGeometry args={[0.16, 0.16, 0.055]} />
      <meshBasicMaterial color="#b9ff32" toneMapped={false} />
    </mesh>
  );
}

function ArrowObject({ pointerRef, progressRef, reducedMotion }) {
  const group = useRef();

  useFrame((state, delta) => {
    if (!group.current) return;
    const time = state.clock.elapsedTime;
    const motion = reducedMotion ? 0 : 1;
    group.current.rotation.z = THREE.MathUtils.damp(
      group.current.rotation.z,
      -0.78 + pointerRef.current.x * 0.08 * motion,
      3,
      delta,
    );
    group.current.rotation.y = THREE.MathUtils.damp(
      group.current.rotation.y,
      pointerRef.current.y * 0.25 * motion,
      3,
      delta,
    );
    group.current.position.y =
      -1.75 + Math.sin(time * 0.8) * 0.11 * motion + progressRef.current * 0.4;
  });

  return (
    <group ref={group} position={[5.9, -1.75, 1.8]} rotation={[0.2, -0.2, -0.78]}>
      <mesh>
        <coneGeometry args={[0.82, 1.9, 3]} />
        <meshPhysicalMaterial
          color="#4d73ff"
          roughness={0.22}
          metalness={0.14}
          clearcoat={1}
          clearcoatRoughness={0.08}
          transparent
          opacity={0.94}
        />
      </mesh>
      <mesh position={[-0.08, -0.53, -0.08]} rotation={[0, 0, 0.18]}>
        <boxGeometry args={[0.45, 0.9, 0.28]} />
        <meshPhysicalMaterial color="#3553c8" roughness={0.25} clearcoat={0.8} />
      </mesh>
    </group>
  );
}

function LightRig({ pointerRef, progressRef, reducedMotion }) {
  const keyLight = useRef();

  useFrame((_, delta) => {
    if (!keyLight.current) return;
    const pointer = pointerRef.current;
    const motion = reducedMotion ? 0 : 1;
    keyLight.current.position.x = THREE.MathUtils.damp(
      keyLight.current.position.x,
      pointer.x * 4.5 * motion + 2,
      3.7,
      delta,
    );
    keyLight.current.position.y = THREE.MathUtils.damp(
      keyLight.current.position.y,
      pointer.y * 3 * motion + 4 - progressRef.current,
      3.7,
      delta,
    );
  });

  return (
    <>
      <ambientLight intensity={1.25} color="#8192ff" />
      <directionalLight position={[-4, 6, 7]} intensity={2.2} color="#b8c6ff" />
      <pointLight
        ref={keyLight}
        position={[2, 4, 5]}
        intensity={90}
        distance={18}
        decay={2}
        color="#d9e1ff"
      />
      <pointLight
        position={[-5, -3, 4]}
        intensity={55}
        distance={14}
        decay={2}
        color="#3134ff"
      />
    </>
  );
}

function RipplePostProcessing({ rippleQueueRef, reducedMotion }) {
  const { gl, scene, camera, size } = useThree();
  const rippleSlots = useRef(
    Array.from({ length: MAX_RIPPLES }, () => ({
      x: -10,
      y: -10,
      age: 10,
      strength: 0,
    })),
  );
  const nextSlot = useRef(0);

  const pipeline = useMemo(() => {
    const composer = new EffectComposer(gl);
    const renderPass = new RenderPass(scene, camera);
    const ripplePass = new ShaderPass(RippleShader);
    const outputPass = new OutputPass();

    composer.addPass(renderPass);
    composer.addPass(ripplePass);
    composer.addPass(outputPass);

    return { composer, renderPass, ripplePass, outputPass };
  }, [camera, gl, scene]);

  useEffect(() => {
    const pixelRatio = Math.min(gl.getPixelRatio(), 1.5);
    pipeline.composer.setPixelRatio(pixelRatio);
    pipeline.composer.setSize(size.width, size.height);
    pipeline.ripplePass.uniforms.uResolution.value.set(
      size.width * pixelRatio,
      size.height * pixelRatio,
    );
  }, [gl, pipeline, size.height, size.width]);

  useEffect(
    () => () => {
      pipeline.renderPass.dispose();
      pipeline.ripplePass.dispose();
      pipeline.outputPass.dispose();
      pipeline.composer.dispose();
    },
    [pipeline],
  );

  useFrame((_, delta) => {
    const slots = rippleSlots.current;

    if (reducedMotion) {
      rippleQueueRef.current.length = 0;
      for (const slot of slots) {
        slot.age = 10;
        slot.strength = 0;
      }
    } else {
      const queuedRipples = rippleQueueRef.current.splice(0);

      for (const ripple of queuedRipples) {
        const slot = slots[nextSlot.current];
        slot.x = ripple.x;
        slot.y = ripple.y;
        slot.age = 0;
        slot.strength = ripple.strength;
        nextSlot.current = (nextSlot.current + 1) % MAX_RIPPLES;
      }

      for (const slot of slots) {
        slot.age += delta;
        if (slot.age > 1.55) slot.strength = 0;
      }
    }

    const uniformRipples = pipeline.ripplePass.uniforms.uRipples.value;

    for (let index = 0; index < MAX_RIPPLES; index += 1) {
      const slot = slots[index];
      uniformRipples[index].set(
        slot.x,
        slot.y,
        slot.age,
        slot.strength,
      );
    }

    pipeline.composer.render(delta);
  }, 1);

  return null;
}

function WebGLScene({ pointerRef, progressRef, reducedMotion }) {
  return (
    <>
      <color attach="background" args={["#07165e"]} />
      <fog attach="fog" args={["#07165e", 13, 24]} />
      <LightRig
        pointerRef={pointerRef}
        progressRef={progressRef}
        reducedMotion={reducedMotion}
      />
      <Suspense fallback={null}>
        <WordMark
          pointerRef={pointerRef}
          progressRef={progressRef}
          reducedMotion={reducedMotion}
        />
      </Suspense>
      <StickerField
        pointerRef={pointerRef}
        progressRef={progressRef}
        reducedMotion={reducedMotion}
      />
      <ArrowObject
        pointerRef={pointerRef}
        progressRef={progressRef}
        reducedMotion={reducedMotion}
      />
      <PointerGlyph pointerRef={pointerRef} reducedMotion={reducedMotion} />
    </>
  );
}

function GlobeMark() {
  return (
    <svg viewBox="0 0 48 24" aria-hidden="true">
      <ellipse cx="24" cy="12" rx="21" ry="10" />
      <ellipse cx="24" cy="12" rx="10" ry="10" />
      <path d="M3 12h42M24 2v20" />
    </svg>
  );
}

function CrosshairGrid() {
  return (
    <div className="grid-overlay" aria-hidden="true">
      {Array.from({ length: 12 }, (_, index) => (
        <i key={`x-${index}`} style={{ "--x": `${((index + 1) / 13) * 100}%` }} />
      ))}
    </div>
  );
}

function App() {
  const rootRef = useRef();
  const coordinateRef = useRef();
  const clockRef = useRef();
  const pointerRef = useRef({ x: 0, y: 0, clientX: 0, clientY: 0 });
  const progressRef = useRef(0);
  const rippleQueueRef = useRef([]);
  const lastRippleRef = useRef({ x: 0.5, y: 0.5, time: 0 });
  const lenisRef = useRef();
  const reducedMotion = useReducedMotion();
  const [paperMode, setPaperMode] = useState(false);

  const updateTime = useCallback(() => {
    if (!clockRef.current) return;
    const time = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Asia/Shanghai",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(new Date());
    clockRef.current.textContent = `GMT+8 CN ${time}`;
  }, []);

  useEffect(() => {
    updateTime();
    const timer = window.setInterval(updateTime, 15_000);
    return () => window.clearInterval(timer);
  }, [updateTime]);

  useEffect(() => {
    const onPointerMove = (event) => {
      const x = (event.clientX / window.innerWidth - 0.5) * 2;
      const y = -(event.clientY / window.innerHeight - 0.5) * 2;
      pointerRef.current = {
        x,
        y,
        clientX: event.clientX,
        clientY: event.clientY,
      };

      if (!reducedMotion) {
        const rippleX = event.clientX / window.innerWidth;
        const rippleY = 1 - event.clientY / window.innerHeight;
        const now = event.timeStamp / 1000;
        const previous = lastRippleRef.current;
        const dx = rippleX - previous.x;
        const dy = rippleY - previous.y;
        const distance = Math.hypot(dx, dy);
        const elapsed = Math.max(now - previous.time, 0.016);

        if (distance > 0.012 && elapsed > 0.018) {
          const steps = clamp(Math.ceil(distance / 0.035), 1, 4);
          const speed = distance / elapsed;
          const strength = clamp(0.32 + speed * 0.1, 0.38, 1);

          for (let step = 1; step <= steps; step += 1) {
            const mix = step / steps;
            rippleQueueRef.current.push({
              x: THREE.MathUtils.lerp(previous.x, rippleX, mix),
              y: THREE.MathUtils.lerp(previous.y, rippleY, mix),
              strength: strength * (0.72 + mix * 0.28),
            });
          }

          if (rippleQueueRef.current.length > 28) {
            rippleQueueRef.current.splice(
              0,
              rippleQueueRef.current.length - 28,
            );
          }

          lastRippleRef.current = { x: rippleX, y: rippleY, time: now };
        }
      }

      if (coordinateRef.current) {
        const screenX = String(Math.round(event.clientX)).padStart(4, "0");
        const screenY = String(Math.round(event.clientY)).padStart(4, "0");
        coordinateRef.current.textContent = `${screenX} X ${screenY} Y`;
      }
    };

    window.addEventListener("pointermove", onPointerMove, { passive: true });
    return () => window.removeEventListener("pointermove", onPointerMove);
  }, [reducedMotion]);

  useEffect(() => {
    const lenis = new Lenis({
      autoRaf: true,
      smoothWheel: !reducedMotion,
      duration: reducedMotion ? 0 : 1.08,
      easing: (time) => Math.min(1, 1.001 - 2 ** (-10 * time)),
      wheelMultiplier: 0.92,
    });
    lenisRef.current = lenis;

    const updateProgress = () => {
      const maxScroll = Math.max(1, document.documentElement.scrollHeight - innerHeight);
      const progress = clamp(window.scrollY / maxScroll, 0, 1);
      progressRef.current = progress;

      if (rootRef.current) {
        rootRef.current.style.setProperty("--hero-y", `${progress * -18}vh`);
        rootRef.current.style.setProperty("--hero-scale", `${1 - progress * 0.035}`);
        rootRef.current.style.setProperty("--top-opacity", `${clamp(1 - progress * 3.4, 0, 1)}`);
        rootRef.current.style.setProperty("--secondary-opacity", `${clamp((progress - 0.32) / 0.35, 0, 1)}`);
        rootRef.current.style.setProperty("--secondary-y", `${(1 - progress) * 42}px`);
        rootRef.current.style.setProperty("--thumb-y", `${progress * 154}px`);
        rootRef.current.style.setProperty("--phase", progress.toFixed(3));
      }
    };

    lenis.on("scroll", updateProgress);
    updateProgress();
    window.addEventListener("resize", updateProgress);

    return () => {
      window.removeEventListener("resize", updateProgress);
      lenis.destroy();
    };
  }, [reducedMotion]);

  const replay = () => {
    lenisRef.current?.scrollTo(0, {
      duration: reducedMotion ? 0 : 1.15,
    });
  };

  return (
    <main
      ref={rootRef}
      id="hero"
      className={`experience ${paperMode ? "paper-mode" : ""}`}
    >
      <section className="hero-shell" aria-label="Interactive motion hero">
        <div className="canvas-layer" aria-hidden="true">
          <Canvas
            dpr={[1, 2]}
            camera={{ position: [0, 0, 13.8], fov: 38, near: 0.1, far: 60 }}
            gl={{
              antialias: true,
              alpha: false,
              powerPreference: "high-performance",
              toneMapping: THREE.ACESFilmicToneMapping,
              toneMappingExposure: 1.12,
            }}
          >
            <WebGLScene
              pointerRef={pointerRef}
              progressRef={progressRef}
              reducedMotion={reducedMotion}
            />
            <RipplePostProcessing
              rippleQueueRef={rippleQueueRef}
              reducedMotion={reducedMotion}
            />
          </Canvas>
        </div>

        <CrosshairGrid />

        <header className="hud">
          <div className="hud-top">
            <a className="wordmark-link" href="#hero" onClick={replay}>
              MOTION.FIELD
            </a>

            <nav aria-label="Hero controls">
              <span>STUDY[01]</span>
              <button type="button" onClick={() => setPaperMode((value) => !value)}>
                THEME[{paperMode ? "B" : "A"}]
              </button>
              <button type="button" onClick={replay}>
                REPLAY[↟]
              </button>
            </nav>
          </div>

          <div className="hud-bottom">
            <span ref={clockRef}>GMT+8 CN --:--</span>
            <span ref={coordinateRef}>0000 X 0000 Y</span>
            <GlobeMark />
          </div>
        </header>

        <div className="hero-copy">
          <p className="discipline">Design &amp;<br />Engineering</p>
          <p className="manifesto">Thinking in systems.<br />Moving with intent.</p>
          <p className="bio">
            An independent motion study combining spatial typography,
            pointer physics, and a scroll-driven interface.
          </p>

          <h1>
            I SHAPE<br />
            CRAFT &amp; MOTION<br />
            INTO DIGITAL FORM
          </h1>
        </div>

        <p className="secondary-statement" aria-hidden="true">
          SCROLL IS<br />
          A MATERIAL.
        </p>

        <div className="scroll-rail" aria-hidden="true">
          <span className="scroll-thumb" />
        </div>

        <div className="scroll-cue" aria-hidden="true">
          <span>WHEEL TO BEND THE FIELD</span>
          <i />
        </div>
      </section>
    </main>
  );
}

createRoot(document.getElementById("root")).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
